import {
  putAsset, getAsset, deleteAsset, putFlight, getFlight, listAssetsByFlight,
} from "./store";
import { requestPersistedStorage } from "./persist";
import { hasSpaceFor } from "./quota";
import { reassembleRung } from "../export/hlsRung";
import type { OfflineAssetRecord, OfflineFlightRecord } from "./schema";
import type { Annotations, FlightMeta } from "../api/types";
import { OFFLINE_BASEMAP_PROVIDER } from "../map/basemaps";

// Heuristic byte estimates for assets whose true size we don't know until we
// fetch them. These are conservative ballparks tuned for the typical
// helicopter-flight payload; the goal is to keep `hasSpaceFor` from passing
// when the cache obviously won't fit, not to be exact. Tweak if observed
// real-world sizes diverge meaningfully.
const TILE_BYTES_HEURISTIC = 30 * 1024;       // 30 KB per OSM/satellite tile
const FRAME_BYTES_HEURISTIC = 200 * 1024;     // 200 KB per frame variant (full+thumb each)
const PANORAMA_BYTES_HEURISTIC = 1.5 * 1024 * 1024; // 1.5 MB per panorama JPEG
const VIDEO_BYTES_HEURISTIC = 50 * 1024 * 1024; // 50 MB fallback when we can't HEAD the video
const SMALL_ASSET_BYTES_HEURISTIC = 64 * 1024;  // 64 KB headroom each for track/meta/thumb/annotations
const PREVIEW_BYTES_HEURISTIC = 25 * 1024 * 1024; // 25 MB fallback for the 320p scrub preview
const OVERLAY_BYTES_HEURISTIC = 2 * 1024 * 1024;    // 2 MB per client KMZ
const SET_TRACK_BYTES_HEURISTIC = 512 * 1024;       // 512 KB per sibling track.json

/** Matches audit.ts's CURRENT_SCHEMA — bumped if the annotations baseline
 *  format changes. */
const SCHEMA_VERSION = 1;

/**
 * Thrown when an IndexedDB write fails with `DOMException("QuotaExceededError")`.
 *
 * Callers should surface a dedicated message (e.g. routing the user to
 * `/offline` to free space) rather than the generic download-failed alert,
 * because the failure is recoverable by evicting other cached flights.
 */
export class OfflineQuotaExceededError extends Error {
  constructor(message = "offline storage quota exceeded") {
    super(message);
    this.name = "OfflineQuotaExceededError";
  }
}

function isQuotaExceeded(err: unknown): boolean {
  return err instanceof DOMException && err.name === "QuotaExceededError";
}

/** True for the rejection a cancelled fetch (or throwIfAborted) produces.
 *  Matched by `name` rather than `instanceof DOMException` because the fetch
 *  implementation is not guaranteed to reject with one. */
function isAbortError(err: unknown): boolean {
  return typeof err === "object" && err !== null
    && (err as { name?: unknown }).name === "AbortError";
}

export interface DownloadInput {
  flightId: string;
  team: string;
  videoQuality: string;
  meta: FlightMeta;
  assetUrls: {
    /** The single file to store under `:video`. Absent for an HLS rung, whose
     *  blob is reassembled from `videoReassemble` instead. */
    video?: string;
    /** 320p preview.mp4 — cached under `:preview` for the scrub videos. */
    preview?: string;
    track: string;
    meta: string;
    thumbnail: string;
    annotationsBaseline: Annotations;
  };
  tiles: Array<{ url: string; basemap: string; z: number; x: number; y: number }>;
  /** Presigned URLs expire (frames: 5 min; panoramas: 1 h), and the video
   *  phase alone can run longer than that, so URLs are resolved lazily in
   *  their own phase. */
  panoramas: Array<{ id: string; url: () => Promise<string> }>;
  frames: Array<{ id: string; urls: () => Promise<{ full: string; thumb: string }> }>;
  /** Team KMZ overlays (Plan 13). Cached under this flight's keys; `bytes()`
   *  resolving to null means the overlay is gone and is skipped, not fatal. */
  overlays: Array<{ id: string; name: string; bytes: () => Promise<ArrayBuffer | null> }>;
  /** detail.set with trackUrls blanked; undefined when not in a set. Stored
   *  verbatim as JSON under `${flightId}:set`. */
  setInfo?: unknown;
  /** Sibling flights' track.json URLs, for the offline ghost tracks. */
  setTracks: Array<{ flightId: string; url: string }>;
  onProgress: (done: number, total: number, label: string) => void;
  /** When set, the video asset is a reassembled HLS rung rather than a direct
   *  URL fetch. download stores the concatenated fMP4 under `${flightId}:video`. */
  videoReassemble?: { playlistUrl: string; authToken: string; workerOrigin: string };
  /** Real byte estimate for the video (HLS: bandwidth/8 * duration). Overrides
   *  the VIDEO_BYTES_HEURISTIC in the quota gate when present. */
  videoBytesEstimate?: number;
  /** Real byte estimate for preview.mp4 (300 kbps × duration). */
  previewBytesEstimate?: number;
}

export interface DownloadOpts { signal?: AbortSignal }

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw new DOMException("offline download aborted", "AbortError");
}

async function fetchBlob(url: string, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status} on ${url}`);
  return await res.blob();
}

/** Unconditional write — overwrites whatever is under `key`. Only for assets
 *  that must stay in step with the rest of the cache (the overlays manifest
 *  and the flight-set block); everything else goes through putUncached so a
 *  resume skips it. */
async function putOverwrite(
  key: string,
  flightId: string,
  kind: OfflineAssetRecord["kind"],
  blob: Blob,
  extra?: Partial<OfflineAssetRecord>,
): Promise<void> {
  try {
    await putAsset({
      key,
      flightId,
      kind,
      blob,
      contentType: blob.type || "application/octet-stream",
      byteSize: blob.size,
      storedAt: new Date().toISOString(),
      ...extra,
    });
  } catch (err) {
    if (isQuotaExceeded(err)) {
      throw new OfflineQuotaExceededError();
    }
    throw err;
  }
}

async function putUncached(
  key: string,
  flightId: string,
  kind: OfflineAssetRecord["kind"],
  blob: Blob,
  extra?: Partial<OfflineAssetRecord>,
): Promise<void> {
  const existing = await getAsset(key);
  if (existing !== null) return; // resumption: skip already-cached
  await putOverwrite(key, flightId, kind, blob, extra);
}

async function putFlightRecord(record: OfflineFlightRecord): Promise<void> {
  try {
    await putFlight(record);
  } catch (err) {
    if (isQuotaExceeded(err)) throw new OfflineQuotaExceededError();
    throw err;
  }
}

/** True when an already-stored `:video` blob was built from the quality the
 *  caller is asking for — the only case in which resuming it is honest.
 *
 *  Records written before 2026-08-27 carry no `variant`, so the flight record
 *  that was already on disk when this download STARTED is the only evidence of
 *  what the blob holds: trust it when it agrees, and rebuild otherwise. It has
 *  to be the pre-existing record — the `partial` placeholder this download
 *  writes carries the requested quality and would rubber-stamp any blob. */
function videoVariantMatches(
  existing: OfflineAssetRecord,
  input: DownloadInput,
  priorFlight: OfflineFlightRecord | null,
): boolean {
  if (existing.variant !== undefined) return existing.variant === input.videoQuality;
  return priorFlight !== null && priorFlight.videoQuality === input.videoQuality;
}

/** Estimate the total bytes a DownloadInput is expected to consume. Uses
 *  heuristics for assets we can't HEAD up front. Exported for testing. */
export function estimateDownloadBytes(input: DownloadInput): number {
  // 4 small fixed assets (track, meta, thumbnail) + annotations baseline,
  // each given a 64 KB headroom slot. Video gets its own (much larger)
  // heuristic.
  const small = 4 * SMALL_ASSET_BYTES_HEURISTIC;
  const video = input.videoBytesEstimate ?? VIDEO_BYTES_HEURISTIC;
  const preview =
    input.assetUrls.preview !== undefined
      ? (input.previewBytesEstimate ?? PREVIEW_BYTES_HEURISTIC)
      : 0;
  const tiles = input.tiles.length * TILE_BYTES_HEURISTIC;
  const panos = input.panoramas.length * PANORAMA_BYTES_HEURISTIC;
  const frames = input.frames.length * 2 * FRAME_BYTES_HEURISTIC;
  const overlays = input.overlays.length * OVERLAY_BYTES_HEURISTIC;
  const setTracks = input.setTracks.length * SET_TRACK_BYTES_HEURISTIC;
  return small + video + preview + tiles + panos + frames + overlays + setTracks;
}

export async function downloadFlightForOffline(
  input: DownloadInput,
  opts: DownloadOpts = {},
): Promise<void> {
  const { signal } = opts;
  await requestPersistedStorage();

  // Pre-flight quota gate. hasSpaceFor returns true when navigator.storage is
  // unavailable (quota === 0), so we still fall through to the put attempts
  // on browsers that don't expose StorageManager.estimate.
  //
  // Discount what this flight already occupies: on a resume those bytes are
  // counted in `usage` AND in the estimate, so the gate would demand room for
  // a second copy and refuse a resume that only has a few MB left to fetch.
  const alreadyStored = (await listAssetsByFlight(input.flightId))
    .reduce((sum, a) => sum + a.byteSize, 0);
  const estimatedBytes = Math.max(estimateDownloadBytes(input) - alreadyStored, 0);
  if (!(await hasSpaceFor(estimatedBytes))) {
    throw new OfflineQuotaExceededError(
      `offline storage quota too low for estimated ${Math.ceil(estimatedBytes / 1_000_000)} MB`,
    );
  }

  // Optimistic counts for the `partial` placeholder below. auditFlight
  // short-circuits on `partial`, so an overlay that turns out to be gone
  // costs nothing here; the FINAL record re-derives both new counts from
  // what actually landed in the store.
  const assetCounts = {
    tiles: input.tiles.length,
    panoramas: input.panoramas.length,
    frames: input.frames.length,
    preview: (input.assetUrls.preview !== undefined ? 1 : 0) as 0 | 1,
    overlays: input.overlays.length,
    setTracks: input.setTracks.length,
  };

  // Claim the flight up front with `partial: true`. A cancelled or failed 4K
  // download can strand well over a gigabyte of assets, and without a record
  // the Manage page cannot see them — let alone remove them. The final write
  // at the end overwrites this with the real record (no `partial`). An
  // existing record (resume, or a re-download at another quality) is left
  // alone so a complete cache is never downgraded mid-flight.
  const priorFlight = await getFlight(input.flightId);
  if (priorFlight === null) {
    await putFlightRecord({
      flightId: input.flightId,
      team: input.team,
      cachedAt: new Date().toISOString(),
      videoQuality: input.videoQuality,
      byteSizeApprox: 0,
      schemaVersion: SCHEMA_VERSION,
      partial: true,
      basemapProvider: OFFLINE_BASEMAP_PROVIDER,
      meta: input.meta,
      assetCounts,
    });
  }

  // Progress is reported BEFORE each fetch with the asset about to be fetched,
  // so a stall or failure is attributed to the right item. `total` is
  // adjusted once when the rung's variant playlist reveals its segment count
  // (each segment becomes a step in place of the single video slot).
  let total =
    4 + // video, track, meta, thumbnail
    1 + // annotations baseline
    (input.assetUrls.preview !== undefined ? 1 : 0) +
    input.tiles.length + input.panoramas.length + input.frames.length * 2 +
    // overlays: one step each plus one for the manifest they share
    (input.overlays.length > 0 ? input.overlays.length + 1 : 0) +
    (input.setInfo !== undefined ? 1 : 0) +
    input.setTracks.length;
  let done = 0;
  const begin = (label: string): void => input.onProgress(done, total, label);
  const finish = (): void => { done += 1; };

  // 1. annotations baseline (JSON; small)
  begin("annotations");
  const baselineBlob = new Blob(
    [JSON.stringify(input.assetUrls.annotationsBaseline)],
    { type: "application/json" },
  );
  await putUncached(
    `${input.flightId}:annotations-baseline`,
    input.flightId,
    "annotations-baseline",
    baselineBlob,
  );
  finish();

  throwIfAborted(signal);

  // Video: either a reassembled HLS rung or a direct URL fetch. The outer
  // getAsset guard is load-bearing for resumption: putUncached has its own
  // internal skip, but we must ALSO short-circuit the (expensive, hundreds-of-
  // segment) reassembleRung call before it runs, not just the final put.
  // NOTE: reassembleRung accumulates the whole rung into one Blob in memory
  // before it is written to IndexedDB. Browser Blob parts are normally backed
  // by disk so this is usually metadata-only, but a multi-GB rung has an
  // in-memory ceiling on constrained devices. Desktop is the target (iOS is a
  // non-goal); revisit with chunked writes only if that changes.
  const videoKey = `${input.flightId}:video`;
  // A `:video` left by an earlier attempt at a DIFFERENT quality must be
  // rebuilt: otherwise "download at preview, then rebuild at 4K" would skip
  // reassembly and relabel a 320p blob as the 4K rung (and vice versa).
  let existingVideo = await getAsset(videoKey);
  if (existingVideo !== null && !videoVariantMatches(existingVideo, input, priorFlight)) {
    await deleteAsset(videoKey);
    existingVideo = null;
  }
  if (existingVideo === null) {
    if (input.videoReassemble !== undefined) {
      const { playlistUrl, authToken, workerOrigin } = input.videoReassemble;
      let segmentTotal = 1;
      begin("video playlist");
      const blob = await reassembleRung(playlistUrl, {
        authToken, workerOrigin, signal,
        onProgress: (d, t) => {
          if (d === 0) { total += t - 1; segmentTotal = t; return; } // playlist parsed
          input.onProgress(done + d, total, `video segment ${d}/${t}`);
        },
      });
      await putUncached(videoKey, input.flightId, "video", blob, {
        variant: input.videoQuality,
      });
      done += segmentTotal;
    } else {
      if (input.assetUrls.video === undefined) {
        throw new Error(
          `flight "${input.flightId}" has no video URL and no rung to reassemble `
          + `for quality "${input.videoQuality}"`,
        );
      }
      begin("video");
      const blob = await fetchBlob(input.assetUrls.video, signal);
      await putUncached(videoKey, input.flightId, "video", blob, {
        variant: input.videoQuality,
      });
      finish();
    }
  } else {
    begin("video (cached)");
    finish();
  }

  // 3. Remaining small assets (track, meta, thumbnail).
  const single: Array<[string, OfflineAssetRecord["kind"], string]> = [
    [`${input.flightId}:track`,     "track",     input.assetUrls.track],
    [`${input.flightId}:meta`,      "meta",      input.assetUrls.meta],
    [`${input.flightId}:thumbnail`, "thumbnail", input.assetUrls.thumbnail],
  ];
  for (const [key, kind, url] of single) {
    throwIfAborted(signal);
    begin(kind);
    if ((await getAsset(key)) === null) {
      await putUncached(key, input.flightId, kind, await fetchBlob(url, signal));
    }
    finish();
  }

  // 3b. 320p preview (scrub videos). Its own key so it never collides with
  // the main video's :video record.
  if (input.assetUrls.preview !== undefined) {
    begin("preview");
    const previewKey = `${input.flightId}:preview`;
    if ((await getAsset(previewKey)) === null) {
      // At quality "preview" the main video IS preview.mp4, so the blob we
      // just stored under :video is the same file — copy it rather than
      // pulling ~25 MB over the wire a second time.
      const sameFile = input.assetUrls.preview === input.assetUrls.video;
      const cachedVideo = sameFile ? await getAsset(videoKey) : null;
      if (cachedVideo !== null) {
        // Carry the stored record's own size/type rather than re-reading them
        // off a blob that has already been through IndexedDB.
        await putUncached(previewKey, input.flightId, "preview", cachedVideo.blob, {
          byteSize: cachedVideo.byteSize,
          contentType: cachedVideo.contentType,
        });
      } else {
        const blob = await fetchBlob(input.assetUrls.preview, signal);
        await putUncached(previewKey, input.flightId, "preview", blob);
      }
    }
    finish();
  }

  for (const [i, tile] of input.tiles.entries()) {
    throwIfAborted(signal);
    begin(`tile ${i + 1}/${input.tiles.length}`);
    const key = `${input.flightId}:tile:${tile.basemap}:${tile.z}:${tile.x}:${tile.y}`;
    if ((await getAsset(key)) === null) {
      await putUncached(key, input.flightId, "tile", await fetchBlob(tile.url, signal));
    }
    finish();
  }
  for (const pano of input.panoramas) {
    throwIfAborted(signal);
    begin(`panorama ${pano.id}`);
    const key = `${input.flightId}:pano:${pano.id}`;
    if ((await getAsset(key)) === null) {
      const url = await pano.url();
      await putUncached(key, input.flightId, "pano", await fetchBlob(url, signal));
    }
    finish();
  }
  for (const [i, frame] of input.frames.entries()) {
    throwIfAborted(signal);
    begin(`frame full ${i + 1}/${input.frames.length}`);
    const fullKey = `${input.flightId}:frame-full:${frame.id}`;
    const thumbKey = `${input.flightId}:frame-thumb:${frame.id}`;
    const needFull = (await getAsset(fullKey)) === null;
    const needThumb = (await getAsset(thumbKey)) === null;
    // Resolve both presigns at once (one round-trip pair) only if something is missing.
    const urls = needFull || needThumb ? await frame.urls() : null;
    if (needFull && urls !== null) {
      await putUncached(fullKey, input.flightId, "frame-full", await fetchBlob(urls.full, signal));
    }
    finish();
    begin(`frame thumb ${i + 1}/${input.frames.length}`);
    if (needThumb && urls !== null) {
      const blob = await fetchBlob(urls.thumb, signal);
      await putUncached(thumbKey, input.flightId, "frame-thumb", blob);
    }
    finish();
  }

  // Team KMZ overlays (Plan 13 retrofit) — duplicated under this flight's
  // keys so per-flight eviction needs no refcounting. These are ancillary
  // assets fetched AFTER the video/tiles/panoramas/frames, so one gone
  // overlay (null bytes, a rejected fetch, an expired presign) is skipped
  // rather than discarding an otherwise-complete multi-GB download. An
  // abort is the one rejection that still propagates: cancelling must
  // cancel.
  if (input.overlays.length > 0) {
    const listed: Array<{ id: string; name: string }> = [];
    for (const o of input.overlays) {
      throwIfAborted(signal);
      begin(`overlay ${o.id}`);
      const key = `${input.flightId}:overlay:${o.id}`;
      if ((await getAsset(key)) !== null) {
        listed.push({ id: o.id, name: o.name }); // resume: already cached
      } else {
        let bytes: ArrayBuffer | null = null;
        try {
          bytes = await o.bytes();
        } catch (err) {
          if (signal?.aborted === true || isAbortError(err)) throw err;
          console.warn(`[offline] overlay "${o.id}" skipped`, err);
        }
        if (bytes !== null) {
          await putUncached(
            key,
            input.flightId,
            "overlay",
            new Blob([bytes], { type: "application/vnd.google-earth.kmz" }),
          );
          listed.push({ id: o.id, name: o.name });
        }
      }
      finish();
    }
    // Manifest LAST, from the overlays whose blobs are actually in the cache,
    // and written unconditionally: putUncached's resume skip would otherwise
    // leave a re-download running against a stale manifest that omits a newly
    // cached overlay (or lists one that is gone).
    throwIfAborted(signal);
    begin("overlay manifest");
    await putOverwrite(
      `${input.flightId}:overlays-manifest`,
      input.flightId,
      "overlays-manifest",
      new Blob([JSON.stringify(listed)], { type: "application/json" }),
    );
    finish();
  }

  // Flight-set block + sibling ghost tracks (Plan 13). A sibling track that
  // 404s or whose presign has expired costs its ghost polyline, not the
  // whole download — same skip-but-never-swallow-an-abort rule as overlays.
  if (input.setInfo !== undefined) {
    throwIfAborted(signal);
    begin("set");
    const setBlob = new Blob([JSON.stringify(input.setInfo)], {
      type: "application/json",
    });
    // Overwritten, not resume-skipped, for the same reason as the overlays
    // manifest: this is single-instance JSON describing the set as it is now.
    // A re-download over an un-evicted cache (ViewerPage's "Save for offline"
    // never evicts first) must not keep a membership list that predates a
    // flight being added to — or removed from — the set.
    await putOverwrite(`${input.flightId}:set`, input.flightId, "set", setBlob);
    finish();
  }
  for (const st of input.setTracks) {
    throwIfAborted(signal);
    begin(`set track ${st.flightId}`);
    const key = `${input.flightId}:set-track:${st.flightId}`;
    if ((await getAsset(key)) === null) {
      let blob: Blob | null = null;
      try {
        blob = await fetchBlob(st.url, signal);
      } catch (err) {
        if (signal?.aborted === true || isAbortError(err)) throw err;
        console.warn(`[offline] sibling track "${st.flightId}" skipped`, err);
      }
      if (blob !== null) {
        await putUncached(key, input.flightId, "set-track", blob);
      }
    }
    finish();
  }

  // 4. Final flight record — overwrites the `partial` placeholder above, so
  // only a completed download ends up unflagged.
  // Sum the real byte size of everything now in the store for this flight —
  // including assets carried over from a resumed download — so the Manage page
  // reports an accurate per-flight size rather than a placeholder.
  const stored = await listAssetsByFlight(input.flightId);
  const byteSizeApprox = stored.reduce((sum, a) => sum + a.byteSize, 0);
  // Count what actually landed, not what was offered: an overlay whose
  // `bytes()` returned null was skipped, and writing the optimistic count
  // would make auditFlight report a clean download as evicted.
  const overlayBlobs = stored.filter((a) => a.kind === "overlay").length;
  const setTrackBlobs = stored.filter((a) => a.kind === "set-track").length;
  await putFlightRecord({
    flightId: input.flightId,
    team: input.team,
    cachedAt: new Date().toISOString(),
    videoQuality: input.videoQuality,
    byteSizeApprox,
    schemaVersion: SCHEMA_VERSION,
    basemapProvider: OFFLINE_BASEMAP_PROVIDER,
    meta: input.meta,
    assetCounts: { ...assetCounts, overlays: overlayBlobs, setTracks: setTrackBlobs },
  });

  if (done !== total) {
    console.warn(`[offline] progress mismatch ${done}/${total}`);
  }
  done = total;
  input.onProgress(done, total, "done");
}
